import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Award, 
  BookOpen, 
  ShieldCheck, 
  Quote, 
  Compass, 
  GraduationCap, 
  ExternalLink,
  CheckCircle2 
} from 'lucide-react';
import SanjayDhir from '../../Media/SanjayDhir.png';
import ProfOfYear from '../../Media/18.png';

const areasOfInterest = [
  "Strategic Innovation",
  "Ambidexterity",
  "Strategic Thinking",
  "Strategic Management",
  "Strategic Alliances & Joint Ventures",
  "Technology Transfer",
  "Creative Problem Solving",
  "Management of Change & Transformation"
];

const highlights = [
  { value: "Top 1%", label: "Global Scientist (Stanford/Elsevier)", icon: <Award size={18} className="text-warning" /> },
  { value: "130+", label: "Scopus & Peer-Reviewed Articles", icon: <BookOpen size={18} className="text-cyan" /> },
  { value: "35+", label: "Funded Research Projects", icon: <ShieldCheck size={18} className="text-info" /> },
  { value: "2x", label: "InsideIIM Professor of the Year", icon: <GraduationCap size={18} className="text-warning" /> },
];

export default function Biosketch() {
  const [activePreview, setActivePreview] = useState(null);

  return (
    <div className="container py-5 min-vh-100 position-relative" style={{ zIndex: 1 }}>
      
      {/* 1. HERO & PHILOSOPHY INTRO */}
      <div className="row align-items-center g-5 mb-5">
        
        {/* Left: Bio Title & Theory Core */}
        <div className="col-lg-7 col-md-12" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <Sparkles size={15} className="text-cyan" />
            <span className="small text-light fw-medium">Department of Management Studies • IIT Delhi</span>
          </div>

          <h1 className="display-4 fw-bold text-white mb-2">
            Prof. <span className="text-gradient">Sanjay Dhir</span>
          </h1>
          <p className="lead fs-5 text-secondary mb-4">
            Fellow (Ph.D.), IIM Lucknow • Professor & Area Chair of Strategic Management
          </p>

          {/* Pull Quote Hook */}
          <div className="p-4 rounded-4 glass-card-sm border-glow position-relative mb-4">
            {/* <Quote size={32} className="text-cyan opacity-25 position-absolute top-0 end-0 m-3" /> */}
            <p className="fst-italic text-light fs-5 mb-2 lh-base">
              "Most strategy theory was built by watching firms that had room to move. My work is about strategy when institutions are thin and there is no slack to fund exploration comfortably."
            </p>
            <span className="small text-secondary fw-semibold text-uppercase" style={{ letterSpacing: '0.08em' }}>
              Strategic Ambidexterity & Governance
            </span>
          </div>

          {/* Quick Metrics Bar */}
          <div className="row g-3">
            {highlights.map((h, i) => (
              <div key={i} className="col-6 col-sm-3">
                <div className="p-3 rounded-3 glass-card-sm text-center h-100">
                  <div className="d-flex justify-content-center mb-1">{h.icon}</div>
                  <div className="fw-bold fs-5 text-white mb-0">{h.value}</div>
                  <div className="text-secondary small" style={{ fontSize: '0.72rem' }}>{h.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Primary Framed Portrait */}
        <div className="col-lg-5 col-md-12 text-center" data-aos="fade-left">
          <div className="position-relative p-2 rounded-4 glass-panel border-glow d-inline-block w-100">
            <img 
              src={SanjayDhir} 
              alt="Prof. Sanjay Dhir" 
              className="img-fluid rounded-4 shadow-lg w-100"
              style={{ maxHeight: '800px', objectFit: 'cover' }}
            />
          </div>
        </div>

      </div>

      {/* 2. THE CORE NARRATIVE (Organized by Focus) */}
      <div className="row g-4 mb-5">
        
        {/* Card 1: Theoretical Lens */}
        <div className="col-lg-6" data-aos="fade-up">
          <div className="p-4 rounded-4 glass-panel h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Compass size={20} className="text-cyan" />
              <h4 className="fw-bold text-white mb-0">Theory Under Institutional Thinness</h4>
            </div>
            <p className="text-secondary small lh-lg mb-3">
              Stable institutions, reliable information, and wide executive discretion describe a narrow slice of the world. They do not describe most emerging economies, a hospital allocating oxygen during a surge, or a procurement exchange navigating price discovery across millions of suppliers.
            </p>
            <p className="text-secondary small lh-lg mb-0">
              My work focuses on how firms build and renew dynamic capabilities under resource constraints, utilizing <strong>Interpretive Structural Modelling (ISM)</strong> and its Total Interpretive Extension (TISM) to evaluate variables that move together rather than isolation.
            </p>
          </div>
        </div>

        {/* Card 2: Strategic Equity & Reach */}
        <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
          <div className="p-4 rounded-4 glass-panel h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <ShieldCheck size={20} className="text-cyan" />
              <h4 className="fw-bold text-white mb-0">Equity as an Organisational Output</h4>
            </div>
            <p className="text-secondary small lh-lg mb-3">
              Equity is not a moral consideration attached to strategy post-hoc—it is an organizational capability produced or destroyed by system architecture. The constructs strategy uses to explain why one firm outruns another explain just as cleanly why a healthcare supply line reaches one district and falters in the next.
            </p>
            <p className="text-secondary small lh-lg mb-0">
              My empirical research sits across remote geographies, tribal communities, and gender-disparate environments—not merely as social cases, but because they test strategic capability at its limits.
            </p>
          </div>
        </div>

      </div>

      {/* 3. PEDAGOGY & DISTINCTION (Featuring Image 2: ProfOfYear) */}
      <div className="row align-items-center g-4 p-4 p-md-5 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <div className="col-lg-8">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <GraduationCap size={15} className="text-warning" />
            <span className="small text-warning fw-semibold">Teaching Excellence & Student Recognition</span>
          </div>
          <h3 className="fw-bold text-white mb-3">Professor of the Year & Pedagogy</h3>
          <p className="text-secondary lh-lg mb-3">
            Within IIT Delhi, I coordinate the doctoral programme at the Department of Management Studies, having chaired the MBA programme for six years. I have been recognized with the <strong>IIT Delhi Teaching Excellence Award</strong> and voted <strong>InsideIIM’s Professor of the Year</strong> across premier business schools twice consecutively.
          </p>
          <p className="text-secondary lh-lg mb-0">
            My teaching cases are distributed worldwide through <strong>Richard Ivey School of Business</strong> and <strong>Harvard Business Publishing</strong>, bringing firsthand empirical research from Indian industry into global executive classrooms.
          </p>
        </div>

        <div className="col-lg-4 text-center">
          <motion.div 
            whileHover={{ scale: 1.03 }}
            onClick={() => setActivePreview({ src: ProfOfYear, title: "Professor of the Year Distinction" })}
            className="p-2 rounded-4 glass-card-sm border-glow cursor-pointer position-relative overflow-hidden"
            style={{ cursor: 'pointer', background: 'rgba(7, 10, 19, 0.7)' }}
          >
            <img 
              src={ProfOfYear} 
              alt="InsideIIM Professor of the Year" 
              className="img-fluid rounded-3 shadow"
              style={{ maxHeight: '240px', width: '100%', objectFit: 'contain' }}
            />
            <div 
              className="position-absolute bottom-0 end-0 m-2 px-2 py-1 rounded small text-white"
              style={{ background: 'rgba(0, 0, 0, 0.75)', fontSize: '0.72rem', backdropFilter: 'blur(4px)' }}
            >
              🔍 View Distinction
            </div>
          </motion.div>
        </div>
      </div>

      {/* 4. POLICY & APPLIED ENGAGEMENTS */}
      <div className="p-4 p-md-5 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <h4 className="fw-bold text-white mb-3">National & Global Public Policy</h4>
        <p className="text-secondary lh-lg mb-4">
          Having led over 35 sponsored initiatives, I served as <strong>Institutional Management Partner</strong> for India’s COVID-19 vaccine rollout (supported by the Bill & Melinda Gates Foundation) and acted as technical expert to the <strong>Delhi High Court</strong> and <strong>GNCTD State Committees</strong> on medical oxygen logistics.
        </p>
        
        <div className="row g-3">
          {[
            { org: "Ministry of Health & Family Welfare", note: "Technical advisory on Aarogya Setu national architecture" },
            { org: "PATH & B Medical Systems", note: "Cold chain tracking & oxygen management optimization" },
            { org: "Ministry of Commerce & Industry", note: "Assessment of Government e-Marketplace (GeM) reach" },
            { org: "UQ-IITD Research Academy", note: "Theme Lead: Societal Transformations & Livelihoods" },
          ].map((item, idx) => (
            <div key={idx} className="col-md-6">
              <div className="p-3 rounded-3 glass-card-sm border-start border-3 border-info">
                <h6 className="fw-bold text-white mb-1">{item.org}</h6>
                <p className="text-secondary small mb-0">{item.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5. AREAS OF SPECIALIZATION */}
      <div className="p-4 rounded-4 glass-panel" data-aos="fade-up">
        <h5 className="fw-bold text-white mb-3 d-flex align-items-center gap-2">
          <Sparkles size={18} className="text-warning" /> Areas of Specialization & Research Focus
        </h5>
        <div className="d-flex flex-wrap gap-2">
          {areasOfInterest.map((interest, idx) => (
            <span 
              key={idx} 
              className="badge px-3 py-2 rounded-pill fw-medium"
              style={{
                background: 'rgba(0, 242, 254, 0.08)',
                border: '1px solid rgba(0, 242, 254, 0.25)',
                color: '#e2e8f0',
                fontSize: '0.85rem'
              }}
            >
              {interest}
            </span>
          ))}
        </div>
      </div>

      {/* 6. LIGHTBOX MODAL FOR IMAGES */}
      <AnimatePresence>
        {activePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActivePreview(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(3, 7, 18, 0.9)',
              backdropFilter: 'blur(10px)',
              zIndex: 1060,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              cursor: 'zoom-out'
            }}
          >
            <button
              type="button"
              onClick={() => setActivePreview(null)}
              className="btn btn-outline-light rounded-circle position-absolute top-0 end-0 m-4 d-flex align-items-center justify-content-center"
              style={{ width: '44px', height: '44px', fontSize: '1.25rem', zIndex: 1070 }}
            >
              &times;
            </button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="text-center"
            >
              <img
                src={activePreview.src}
                alt={activePreview.title}
                className="img-fluid rounded-4 shadow-lg border border-secondary border-opacity-50"
                style={{ maxHeight: '80vh', maxWidth: '90vw', objectFit: 'contain' }}
              />
              <p className="mt-3 text-white-50 small mb-0">{activePreview.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}